// Storage Manager Utility
// Handles localStorage quota management, data cleanup, and compression

import { logger } from './logger';

export interface StorageItem {
  key: string;
  data: any;
  timestamp: number;
  size: number;
  priority: 'high' | 'medium' | 'low';
}

export class StorageManager {
  private static readonly MAX_STORAGE_SIZE = 4.5 * 1024 * 1024; // 4.5MB (leave 0.5MB buffer)
  private static readonly CLEANUP_THRESHOLD = 4.0 * 1024 * 1024; // 4MB
  private static readonly MAX_ALERTS = 100;
  private static readonly MAX_RESET_EVENTS = 50;
  private static readonly MAX_ACCUMULATED_RECORDS = 1000;

  // Get current storage usage
  static getStorageUsage(): number {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          total += new Blob([key, value]).size;
        }
      }
    }
    return total;
  }

  // Get available storage space
  static getAvailableStorage(): number {
    return this.MAX_STORAGE_SIZE - this.getStorageUsage();
  }

  // Check if storage is near capacity
  static isStorageNearCapacity(): boolean {
    return this.getStorageUsage() > this.CLEANUP_THRESHOLD;
  }

  // Safe setItem with quota management
  static setItem(key: string, value: any, priority: 'high' | 'medium' | 'low' = 'medium'): boolean {
    try {
      const data = JSON.stringify(value);
      const size = new Blob([key, data]).size;
      
      // Check if we need to clean up storage
      if (this.getStorageUsage() + size > this.MAX_STORAGE_SIZE) {
        this.cleanupStorage(size);
      }
      
      // Try to save the data
      localStorage.setItem(key, data);
      
      // Log storage usage for debugging
      logger.debug(`Storage: Saved ${key} (${size} bytes). Total usage: ${this.getStorageUsage()} bytes`);
      
      return true;
    } catch (error) {
      logger.error(`Storage: Failed to save ${key}:`, error);
      
      // If it's a quota error, try cleanup and retry once
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        logger.log('Storage: Quota exceeded, attempting cleanup...');
        this.cleanupStorage();
        
        try {
          localStorage.setItem(key, JSON.stringify(value));
          logger.log(`Storage: Successfully saved ${key} after cleanup`);
          return true;
        } catch (retryError) {
          logger.error(`Storage: Failed to save ${key} after cleanup:`, retryError);
          return false;
        }
      }
      
      return false;
    }
  }

  // Safe getItem
  static getItem<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      if (!item) {
        return defaultValue;
      }
      
      // Try to parse as JSON first
      try {
        return JSON.parse(item);
      } catch (parseError) {
        // If JSON parsing fails, return the raw string value
        // This handles cases where simple strings like "dark", "en" are stored
        return item as T;
      }
    } catch (error) {
      logger.error(`Storage: Failed to load ${key}:`, error);
      return defaultValue;
    }
  }

  // Special method for authentication data - NEVER removed during cleanup
  static setAuthItem(key: string, value: any): boolean {
    try {
      // Always store auth data as JSON to ensure proper processing
      const data = typeof value === 'string' ? value : JSON.stringify(value);
      const size = new Blob([key, data]).size;
      
      logger.log(`Storage: Storing AUTH data for ${key} (${size} bytes)`);
      
      // For auth data, we use a more aggressive cleanup that NEVER touches auth keys
      if (this.getStorageUsage() + size > this.MAX_STORAGE_SIZE) {
        logger.log(`Storage: Auth data requires cleanup for ${key}`);
        this.cleanupStorageForAuth(size);
      }
      
      // Try to save the auth data
      localStorage.setItem(key, data);
      
      // Log storage usage for debugging
      logger.debug(`Storage: Saved AUTH ${key} (${size} bytes). Total usage: ${this.getStorageUsage()} bytes`);
      
      return true;
    } catch (error) {
      logger.error(`Storage: Failed to save AUTH ${key}:`, error);
      
      // If it's a quota error, try aggressive cleanup and retry once
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        logger.log('Storage: Quota exceeded for auth data, attempting aggressive cleanup...');
        this.cleanupStorageForAuth();
        
        try {
          const data = typeof value === 'string' ? value : JSON.stringify(value);
          localStorage.setItem(key, data);
          logger.log(`Storage: Successfully saved AUTH ${key} after aggressive cleanup`);
          return true;
        } catch (retryError) {
          logger.error(`Storage: Failed to save AUTH ${key} after aggressive cleanup:`, retryError);
          return false;
        }
      }
      
      return false;
    }
  }

  // Aggressive cleanup that NEVER touches authentication data
  private static cleanupStorageForAuth(requiredSpace: number = 0): void {
    logger.log('Storage: Starting AGGRESSIVE cleanup for auth data...');
    
    // Protected keys that should NEVER be removed under any circumstances
    const protectedKeys = [
      'authToken',
      'userData', 
      'refreshToken',
      'tokenExpiration',
      'loginTimestamp'
    ];
    
    const items: StorageItem[] = [];
    
    // Collect all storage items with metadata
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          const size = new Blob([key, value]).size;
          const priority = this.getPriority(key);
          const timestamp = this.getTimestamp(key, value);
          
          // Try to parse as JSON, but handle non-JSON values gracefully
          let parsedData;
          try {
            parsedData = JSON.parse(value);
          } catch (parseError) {
            // If it's not valid JSON, store the raw value
            parsedData = value;
            logger.log(`Storage: Non-JSON value found for key ${key}, storing as raw string`);
          }
          
          items.push({
            key,
            data: parsedData,
            timestamp,
            size,
            priority
          });
        }
      }
    }
    
    // Sort by priority and age (oldest low priority first)
    items.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      
      return a.timestamp - b.timestamp;
    });
    
    // Remove items until we have enough space - NEVER touch auth data
    let freedSpace = 0;
    const targetSpace = requiredSpace + (1024 * 1024); // 1MB buffer
    
    for (const item of items) {
      if (freedSpace >= targetSpace) break;
      
      // NEVER remove protected authentication keys - this is the primary protection
      if (protectedKeys.indexOf(item.key) !== -1) {
        logger.log(`Storage: AUTH PROTECTED - Skipping authentication key ${item.key}`);
        continue;
      }
      
      // NEVER remove high priority items (which includes auth keys)
      if (item.priority === 'high') {
        logger.log(`Storage: AUTH PROTECTED - Skipping high priority key ${item.key}`);
        continue;
      }
      
      // Only remove low priority items for auth data cleanup
      if (item.priority === 'low') {
        try {
          localStorage.removeItem(item.key);
          freedSpace += item.size;
          logger.log(`Storage: AUTH CLEANUP - Removed ${item.key} (${item.size} bytes)`);
        } catch (error) {
          logger.error(`Storage: Failed to remove ${item.key}:`, error);
        }
      } else {
        logger.log(`Storage: AUTH PROTECTED - Skipping ${item.priority} priority key ${item.key}`);
      }
    }
    
    logger.log(`Storage: AUTH Cleanup completed. Freed ${freedSpace} bytes`);
  }

  // Cleanup storage to make room for new data
  private static cleanupStorage(requiredSpace: number = 0): void {
    logger.log('Storage: Starting cleanup...');
    
    // Protected keys that should NEVER be removed under any circumstances
    const protectedKeys = [
      'authToken',
      'userData', 
      'refreshToken',
      'tokenExpiration',
      'loginTimestamp'
    ];
    
    logger.log('Storage: Protected keys:', protectedKeys);
    
    const items: StorageItem[] = [];
    
    // Collect all storage items with metadata
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          const size = new Blob([key, value]).size;
          const priority = this.getPriority(key);
          const timestamp = this.getTimestamp(key, value);
          
          // Try to parse as JSON, but handle non-JSON values gracefully
          let parsedData;
          try {
            parsedData = JSON.parse(value);
          } catch (parseError) {
            // If it's not valid JSON, store the raw value
            parsedData = value;
            logger.log(`Storage: Non-JSON value found for key ${key}, storing as raw string`);
          }
          
          items.push({
            key,
            data: parsedData,
            timestamp,
            size,
            priority
          });
          
          // Special logging for auth-related keys
          if (protectedKeys.indexOf(key) !== -1) {
            logger.log(`Storage: Found protected key ${key} with priority ${priority}, size ${size} bytes`);
          }
        }
      }
    }
    
    // Sort by priority and age (oldest low priority first)
    items.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      
      return a.timestamp - b.timestamp;
    });
    
    // Remove items until we have enough space
    let freedSpace = 0;
    const targetSpace = requiredSpace + (1024 * 1024); // 1MB buffer
    
    logger.log(`Storage: Processing ${items.length} items, target space: ${targetSpace} bytes`);
    
    for (const item of items) {
      if (freedSpace >= targetSpace) break;
      
      logger.log(`Storage: Processing item ${item.key} (priority: ${item.priority}, size: ${item.size} bytes)`);
      
      // NEVER remove protected authentication keys - this is the primary protection
      if (protectedKeys.indexOf(item.key) !== -1) {
        logger.log(`Storage: PROTECTED - Skipping authentication key ${item.key} (protected list)`);
        continue;
      }
      
      // Additional protection: NEVER remove high priority items (which includes auth keys)
      if (item.priority === 'high') {
        logger.log(`Storage: PROTECTED - Skipping high priority key ${item.key} (priority: ${item.priority})`);
        continue;
      }
      
      // Don't remove medium priority items unless absolutely necessary
      if (item.priority === 'medium' && freedSpace < targetSpace * 0.9) {
        logger.log(`Storage: PROTECTED - Skipping medium priority key ${item.key} (not enough space freed yet)`);
        continue;
      }
      
      try {
        localStorage.removeItem(item.key);
        freedSpace += item.size;
        logger.log(`Storage: Removed ${item.key} (${item.size} bytes)`);
      } catch (error) {
        logger.error(`Storage: Failed to remove ${item.key}:`, error);
      }
    }
    
    logger.log(`Storage: Cleanup completed. Freed ${freedSpace} bytes`);
  }

  // Get priority for a storage key
  private static getPriority(key: string): 'high' | 'medium' | 'low' {
    // High priority - essential app data (NEVER remove during cleanup)
    if (key.includes('auth') || 
        key.includes('user') || 
        key.includes('login') || 
        key.includes('token') || 
        key.includes('refresh') ||
        key === 'authToken' ||
        key === 'userData' ||
        key === 'refreshToken' ||
        key === 'tokenExpiration' ||
        key === 'loginTimestamp') {
      return 'high';
    }
    
    // Medium priority - important data
    if (key.includes('alertRules') || key.includes('tour') || key.includes('theme')) {
      return 'medium';
    }
    
    // Low priority - accumulated data, events, etc.
    if (key.includes('accumulated') || key.includes('reset_events') || key.includes('alerts')) {
      return 'low';
    }
    
    return 'medium';
  }

  // Extract timestamp from storage item
  private static getTimestamp(key: string, value: string): number {
    try {
      const data = JSON.parse(value);
      
      // Try to get timestamp from various possible locations
      if (data.timestamp) {
        return new Date(data.timestamp).getTime();
      }
      
      if (data.lastUpdated) {
        return new Date(data.lastUpdated).getTime();
      }
      
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (firstItem.timestamp) {
          return new Date(firstItem.timestamp).getTime();
        }
        if (firstItem.lastUpdated) {
          return new Date(firstItem.lastUpdated).getTime();
        }
      }
      
      // Fallback to current time
      return Date.now();
    } catch {
      return Date.now();
    }
  }

  // Clean up specific data types
  static cleanupAlerts(): void {
    try {
      const alerts = this.getItem<Array<any>>('alerts', []);
      if (alerts.length > this.MAX_ALERTS) {
        // Keep only the most recent alerts
        const sortedAlerts = alerts.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        const trimmedAlerts = sortedAlerts.slice(0, this.MAX_ALERTS);
        this.setItem('alerts', trimmedAlerts, 'low');
        logger.log(`Storage: Cleaned up alerts. Kept ${trimmedAlerts.length} most recent alerts.`);
      }
    } catch (error) {
      logger.error('Storage: Failed to cleanup alerts:', error);
    }
  }

  static cleanupResetEvents(): void {
    try {
      const events = this.getItem<Array<any>>('data_reset_events', []);
      if (events.length > this.MAX_RESET_EVENTS) {
        // Keep only the most recent events
        const sortedEvents = events.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        const trimmedEvents = sortedEvents.slice(0, this.MAX_RESET_EVENTS);
        this.setItem('data_reset_events', trimmedEvents, 'low');
        logger.log(`Storage: Cleaned up reset events. Kept ${trimmedEvents.length} most recent events.`);
      }
    } catch (error) {
      logger.error('Storage: Failed to cleanup reset events:', error);
    }
  }

  static cleanupAccumulatedRecords(): void {
    try {
      const records = this.getItem<Array<any>>('accumulated_data_records', []);
      if (records.length > this.MAX_ACCUMULATED_RECORDS) {
        // Keep only the most recent and highest confidence records
        const sortedRecords = records.sort((a, b) => {
          // Sort by confidence first, then by last updated
          const confidenceDiff = (b.confidence || 0) - (a.confidence || 0);
          if (confidenceDiff !== 0) return confidenceDiff;
          
          return new Date(b.lastUpdated || 0).getTime() - new Date(a.lastUpdated || 0).getTime();
        });
        
        const trimmedRecords = sortedRecords.slice(0, this.MAX_ACCUMULATED_RECORDS);
        this.setItem('accumulated_data_records', trimmedRecords, 'low');
        logger.log(`Storage: Cleaned up accumulated records. Kept ${trimmedRecords.length} highest confidence records.`);
      }
    } catch (error) {
      logger.error('Storage: Failed to cleanup accumulated records:', error);
    }
  }

  // Periodic cleanup
  static schedulePeriodicCleanup(): void {
    // Clean up every 5 minutes
    setInterval(() => {
      if (this.isStorageNearCapacity()) {
        logger.log('Storage: Periodic cleanup triggered');
        this.cleanupAlerts();
        this.cleanupResetEvents();
        this.cleanupAccumulatedRecords();
      }
    }, 5 * 60 * 1000);
  }

  // Get storage statistics
  static getStorageStats(): { usage: number; available: number; percentage: number } {
    const usage = this.getStorageUsage();
    const available = this.getAvailableStorage();
    const percentage = (usage / (usage + available)) * 100;
    
    return { usage, available, percentage };
  }

  // Check if authentication data exists and is valid
  static hasValidAuthData(): boolean {
    try {
      const authToken = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const loginTimestamp = localStorage.getItem('loginTimestamp');
      
      // Check if we have the basic auth data
      if (!authToken || !userData) {
        return false;
      }
      
      // Check if we're within the forced login period
      if (loginTimestamp) {
        const loginTime = parseInt(loginTimestamp, 10);
        const now = Date.now();
        const forcedLoginPeriod = 24 * 60 * 60 * 1000; // 24 hours
        
        if ((now - loginTime) < forcedLoginPeriod) {
          return true;
        }
      }
      
      // Check if token is not expired
      const tokenExpiration = localStorage.getItem('tokenExpiration');
      if (tokenExpiration) {
        const expiration = parseInt(tokenExpiration, 10);
        const now = Date.now();
        
        // Consider token valid if it expires in more than 1 minute
        if (expiration > (now + 60000)) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      logger.error('Storage: Error checking auth data validity:', error);
      return false;
    }
  }
} 